// client/src/features/quotations/LinePickPreview.tsx
//
// THE BEFORE -> AFTER TABLE FOR A CATALOGUE / SUPPLIER PICK.
//
// Owner decisions 2026-09-24: `QuotationDetailPage.handleSearchSelect`'s
// `window.confirm("Are you sure you want to replace this quotation item with
// the shown item?")` said nothing about WHAT would change — a reviewer had to
// remember the line's old price to notice a bad pick. This renders the two
// lines side by side instead, as the body of the in-app confirm
// (`components/ui/ConfirmProvider.tsx`'s `confirm({ body })`).
//
// `before` and `after` are both full `QuotationItemDto`s — `after` is the
// EXACT object the pick is about to write (built by `handleSearchSelect`
// through `linePriceApplication.applyLinePriceFacts` and
// `cataloguePickDetails.pricingDetailsForCataloguePick` BEFORE this component
// is asked to render), never a second computation of the new price. This
// component only reads fields off the two items and formats them for
// display, the same way the quotation grid itself does — through the SAME
// owners (`getAlternateQuotationPricing`, `unitCostTtd`, `ceilPrice`), so the
// preview cannot show a number the grid would not.
import React from 'react';
import type { QuotationItemDto } from '../../lib/quotationApi';
import { ceilPrice } from '../../utils/priceUtils';
import { exactCostText, resolveItemPricing, unitCostTtd } from '../../utils/pricing';
import { getAlternateQuotationPricing } from '../../utils/quotationUnitConversions';
import { ValueChange } from '../../components/ui/ValueChange';
import { pricedFromIdentity } from './itemLookupRows';

/** The packing size the grid's own "Packing Size (Quoting)" column would show for this line. */
function packingSizeOf(item: QuotationItemDto): string {
  const usd = ceilPrice(item.unitPrice || 0);
  const alt = getAlternateQuotationPricing(usd, item.uom || 'pcs', item.itemName || '', item.pricingDetails);
  return alt.quotingSize || '—';
}

/** US$ and, where the line pins one, TT$ — the same pair the Unit Cost Price columns show. */
function unitCostOf(item: QuotationItemDto, ttdToUsdRate: number | null): string {
  // D104.10: the owner's (exact) cost, shown the one way the grid shows it.
  const usd = resolveItemPricing(item).unitCost;
  const ttd = unitCostTtd(item, ttdToUsdRate);
  return `US$${exactCostText(usd)}${ttd != null ? ` (TT$${ttd.toFixed(2)})` : ''}`;
}

export interface LinePickPreviewRow {
  label: string;
  before: string;
  after: string;
}

/**
 * The six facts the owner asked for, before and after — pure, so it is
 * tested without mounting anything. `LinePickPreview` below is a thin table
 * around this.
 */
export function linePickPreviewRows(
  before: QuotationItemDto,
  after: QuotationItemDto,
  ttdToUsdRate: number | null
): LinePickPreviewRow[] {
  return [
    { label: 'Item name', before: before.itemName || '—', after: after.itemName || '—' },
    { label: 'Supplier', before: before.selectedVendor || '—', after: after.selectedVendor || '—' },
    { label: 'Packaging', before: packingSizeOf(before), after: packingSizeOf(after) },
    {
      label: 'Unit',
      before: String(before.uom || '—').toUpperCase(),
      after: String(after.uom || '—').toUpperCase(),
    },
    {
      label: 'Quantity',
      before: before.quantity != null ? String(before.quantity) : '—',
      after: after.quantity != null ? String(after.quantity) : '—',
    },
    { label: 'Unit cost', before: unitCostOf(before, ttdToUsdRate), after: unitCostOf(after, ttdToUsdRate) },
  ];
}

/** The VAT facts a pick writes: the line's VAT, the "VAT?" question, the VAT inside the cost (absent ≠ null). */
function vatFactsOf(item: QuotationItemDto): unknown[] {
  const details = item.pricingDetails || {};
  return [item.vat === true, details.catalogueVatUnset === true, details.costVatPercent];
}

/**
 * D161.1: a pick that leaves the line as it is — the same catalogue row, every row of the table
 * above the same, and the same VAT facts. It is not a Replace; the page offers Confirm match.
 */
export function pickChangesNothing(
  before: QuotationItemDto,
  after: QuotationItemDto,
  ttdToUsdRate: number | null
): boolean {
  const row = pricedFromIdentity(before.pricingDetails).masterCatalogueId;
  if (!row || row !== pricedFromIdentity(after.pricingDetails).masterCatalogueId) return false;
  if (!linePickPreviewRows(before, after, ttdToUsdRate).every((r) => r.before === r.after)) return false;
  const vatAfter = vatFactsOf(after);
  return vatFactsOf(before).every((fact, i) => fact === vatAfter[i]);
}

export interface LinePickPreviewProps {
  before: QuotationItemDto;
  after: QuotationItemDto;
  /** US$ per TT$1, the quotation's own locked rate — or null when it has not loaded. */
  ttdToUsdRate: number | null;
}

/**
 * The confirm dialog's `body` for a catalogue/supplier pick: one row per
 * field, the old value struck through and the new one bold
 * (`ValueChange`, BUSINESS_RULES#value-change-display — the same component
 * the quotation page's own "Cost moved A -> B" box already uses, not a
 * second before/after treatment). A field the pick does not change (most
 * picks keep the vessel's own Item name, 2026-09-22 client call) reads as a
 * single value rather than a pointless "was X, now X".
 */
export const LinePickPreview: React.FC<LinePickPreviewProps> = ({ before, after, ttdToUsdRate }) => {
  const rows = linePickPreviewRows(before, after, ttdToUsdRate);
  // Owner, 2026-09-25 (D64.6 corrected): a pick prices the line, it never
  // renames it. The Supplier cell shows the VENDOR; the catalogue's own item
  // name is in the details card that cell opens (`SupplierDetailsCard`) — the
  // sentence used to say "in the Supplier column", which was false (client
  // 2026-09-26).
  return (
    <>
    <p className="mb-2 text-xs text-slate-600">
      The item description stays as the RFQ wrote it. To see the catalogue&apos;s own item name, click the line&apos;s Supplier cell — it opens the supplier details.
    </p>
    <table className="w-full text-[12.5px]">
      <thead>
        <tr className="text-left text-slate-500">
          <th className="w-28 pb-1.5 pr-2 font-semibold text-xs">Field</th>
          <th className="pb-1.5 font-semibold text-xs">Before → after</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label} className="border-t border-slate-100 align-top">
            <td className="py-1.5 pr-2 font-medium text-slate-600">{row.label}</td>
            <td className="py-1.5 text-slate-800">
              {row.before === row.after ? row.after : <ValueChange from={row.before} to={row.after} />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </>
  );
};

export default LinePickPreview;
